import React, { useState } from "react";
import { Form, Input, Select, InputNumber, Upload, Modal } from "antd";
import { UploadOutlined, PlusOutlined, WarningOutlined } from "@ant-design/icons";
import axios from "axios";
import { BASE_URL } from "../../../constants/urlConstants";
import FinllieLoader from "../../logo/FinllieLoader";
import "./AddPerfume.css";

const { TextArea } = Input;
const { Option }   = Select;

/* ── Category map ── */
const CATEGORY_MAP: Record<string, string[]> = {
  "Mineral": ["250 ml Bottle","500 ml Bottle","1 Litre Bottle","2 Litre Bottle","5 Litre Can","20 Litre Jar"],
  "Sparkling":  ["Mineral Water"],
  "Big Cans":  ["Mineral Water"],
  "Glass Bottle":  ["Mineral Water"],
  "Soft Drinks": ["Lemon Soda","Cola","Orange","Jeera Soda","Limonata"],
};

const MAIN_CATEGORIES = Object.keys(CATEGORY_MAP).sort();

/* ── Bidirectional price helpers ── */
const calcFinalPrice   = (actual: number, disc: number) => actual > 0 ? Math.floor(actual - (actual * disc) / 100) : 0;
const calcDiscount     = (actual: number, final: number) => actual > 0 && final <= actual ? Math.round(((actual - final) / actual) * 100) : 0;

const AddPerfumeForm: React.FC = () => {
  const [form]    = Form.useForm();
  const [loading, setLoading]   = useState(false);
  const [file,    setFile]      = useState<File | null>(null);
  const [subCategories, setSubCategories] = useState<string[]>([]);
  const [errorModal, setErrorModal] = useState<{ open: boolean; title: string; lines: string[] }>({ open: false, title: "", lines: [] });

  const handleCategoryChange = (val: string) => {
    setSubCategories([...(CATEGORY_MAP[val] || [])].sort());
    form.setFieldsValue({ subCategory: undefined });
  };

  /* ── Retail: actual + discount → price ── */
  const onActualPriceChange = (val: number | null) => {
    const actual = val ?? 0;
    const disc   = form.getFieldValue("discount") ?? 0;
    form.setFieldsValue({ price: calcFinalPrice(actual, disc) });
  };

  const onRetailDiscountChange = (val: number | null) => {
    const disc   = val ?? 0;
    const actual = form.getFieldValue("actualPrice") ?? 0;
    form.setFieldsValue({ price: calcFinalPrice(actual, disc) });
  };

  const onRetailPriceChange = (val: number | null) => {
    const final  = val ?? 0;
    const actual = form.getFieldValue("actualPrice") ?? 0;
    form.setFieldsValue({ discount: calcDiscount(actual, final) });
  };

  /* ── Distributor: actual + distDiscount → distPrice ── */
  const onDistDiscountChange = (val: number | null) => {
    const disc   = val ?? 0;
    const actual = form.getFieldValue("actualPrice") ?? 0;
    form.setFieldsValue({ distributorPrice: calcFinalPrice(actual, disc) });
  };

  const onDistPriceChange = (val: number | null) => {
    const final  = val ?? 0;
    const actual = form.getFieldValue("actualPrice") ?? 0;
    form.setFieldsValue({ distributorDiscount: calcDiscount(actual, final) });
  };

  /* ── Submit ── */
  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const normalizedValues = {
        ...values,
        tags: typeof values.tags === "string"
          ? values.tags.split(",").map((t: string) => t.trim().toLowerCase()).join(", ")
          : "",
      };

      const formData = new FormData();
      if (file) formData.append("file", file);
      formData.append("perfume", new Blob([JSON.stringify(normalizedValues)], { type: "application/json" }));

      await axios.post(`${BASE_URL}/api/v1/admin/add`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Success — reset only on success
      form.resetFields();
      setFile(null);
      setSubCategories([]);
      setErrorModal({ open: true, title: "success", lines: ["Product added successfully!"] });
    } catch (error: any) {
      // Error — NO form.resetFields() so user can fix and retry
      const bd = error.response?.data;
      let lines: string[] = ["An unexpected error occurred. Please try again."];
      if (bd) {
        if (typeof bd === "string") lines = [bd];
        else if (bd.message) lines = [bd.message];
        else if (bd.error) lines = Array.isArray(bd.error) ? bd.error : [bd.error];
        else if (typeof bd === "object") lines = Object.entries(bd).map(([k, v]) => k + ": " + v);
      }
      setErrorModal({ open: true, title: "error", lines });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="apf-page">

      {/* Loader */}
      {loading && <FinllieLoader text="Adding product..." />}

      {/* Error / Success Modal */}
      <Modal
        open={errorModal.open}
        footer={null}
        closable={false}
        centered
        width={400}
      >
        <div className="apf-modal-inner">
          <div className={"apf-modal-icon " + (errorModal.title === "success" ? "success" : "error")}>
            {errorModal.title === "success" ? "✓" : "!"}
          </div>
          <h3 className="apf-modal-title">
            {errorModal.title === "success" ? "Product Added Successfully!" : "Failed to Add Product"}
          </h3>
          <div className="apf-modal-lines">
            {errorModal.lines.map((line, i) => (
              <p key={i} className="apf-modal-line">{line}</p>
            ))}
          </div>
          {errorModal.title !== "success" && (
            <p className="apf-modal-hint">Your form data is intact — fix the issue above and try again.</p>
          )}
          <button
            className={"apf-modal-btn " + (errorModal.title === "success" ? "success" : "error")}
            onClick={() => setErrorModal({ open: false, title: "", lines: [] })}
          >
            {errorModal.title === "success" ? "Add Another Product" : "Close & Fix"}
          </button>
        </div>
      </Modal>

      {/* ── Page header ── */}
      <div className="apf-header">
        <PlusOutlined className="apf-header-icon" />
        <span className="apf-header-title">Add New Product</span>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        className="apf-form"
        requiredMark={false}
      >
        <div className="apf-layout">

          {/* ══════════════════════════════════
              COLUMN 1 — Product Info + Category
          ══════════════════════════════════ */}
          <div className="apf-col">

            {/* Section: Basic Info */}
            <div className="apf-card">
              <div className="apf-card-title">Product Information</div>

              <Form.Item label="Product Title" name="perfumeTitle"
                rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="e.g. Finllie 1L Premium Mineral Water" size="large" />
              </Form.Item>

              <Form.Item label="Brand" name="brand"
                rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="e.g. Finllie" size="large" />
              </Form.Item>

              <div className="apf-row">
                <Form.Item label="Main Category" name="category"
                  rules={[{ required: true, message: "Required" }]}>
                  <Select placeholder="Select category" size="large" onChange={handleCategoryChange}>
                    {MAIN_CATEGORIES.map((c) => <Option key={c} value={c}>{c}</Option>)}
                  </Select>
                </Form.Item>

                <Form.Item label="Sub Category" name="subCategory"
                  rules={[{ required: true, message: "Required" }]}>
                  <Select placeholder="Select subcategory" size="large" disabled={!subCategories.length}>
                    {subCategories.map((s) => <Option key={s} value={s}>{s}</Option>)}
                  </Select>
                </Form.Item>
              </div>

              <div className="apf-row">
                <Form.Item label="Pack Type" name="type"
                  rules={[{ required: true, message: "Required" }]}>
                  <Select placeholder="Select pack type" size="large">
                    {["250 ml Bottle","500 ml Bottle","1 Litre Bottle","2 Litre Bottle","5 Litre Can","20 Litre Can","25 Bottles (1L Each)"]
                      .map((t) => <Option key={t} value={t}>{t}</Option>)}
                  </Select>
                </Form.Item>

                <Form.Item label="Unit Type" name="unit"
                  rules={[{ required: true, message: "Required" }]}>
                  <Input placeholder="e.g. pcs, ml, kg" size="large" />
                </Form.Item>
              </div>

              <Form.Item label="Product Description" name="description"
                rules={[{ required: true, message: "Required" }]}>
                <TextArea rows={3} placeholder="Detailed product description" />
              </Form.Item>

              <Form.Item label="Specifications" name="specifications">
                <TextArea rows={2} placeholder="Technical specs, ingredients, etc." />
              </Form.Item>

              <Form.Item label="Tags" name="tags">
                <Input placeholder="e.g. mineral, sparkling, bulk (comma separated)" />
              </Form.Item>
            </div>

            {/* Section: Inventory */}
            <div className="apf-card">
              <div className="apf-card-title">Inventory & Shipping</div>

              <div className="apf-row">
                <Form.Item label="Qty in Stock" name="quantity"
                  rules={[{ required: true, message: "Required" }]}>
                  <InputNumber min={1} placeholder="Available stock" size="large" style={{ width: "100%" }} />
                </Form.Item>

                <Form.Item label="Min Order Qty" name="minQuantity"
                  rules={[{ required: true, message: "Required" }]}>
                  <InputNumber min={1} placeholder="Minimum qty" size="large" style={{ width: "100%" }} />
                </Form.Item>
              </div>

              <div className="apf-row">
                <Form.Item label="Max Order Qty" name="maxQuantity"
                  rules={[{ required: true, message: "Required" }]}>
                  <InputNumber min={1} placeholder="Maximum qty" size="large" style={{ width: "100%" }} />
                </Form.Item>

                <Form.Item label="Shipping Charges (%)" name="shipPer"
                  rules={[{ required: true, message: "Required" }]}>
                  <InputNumber min={0} placeholder="Shipping %" size="large" style={{ width: "100%" }} />
                </Form.Item>
              </div>

              <div className="apf-row">
                <Form.Item label="Delivery Timeline" name="deliveryTimeline"
                  rules={[{ required: true, message: "Required" }]}>
                  <Select placeholder="Select timeline" size="large">
                    <Option value="1 Day">1 Day</Option>
                    <Option value="2 Days">2 Days</Option>
                    <Option value="5 Days">5 Days</Option>
                    <Option value="7 Days">7 Days</Option>
                    <Option value="15 Days">15 Days</Option>
                    <Option value="30 Days">30 Days</Option>
                  </Select>
                </Form.Item>

                <Form.Item label="Product Status" name="status"
                  rules={[{ required: true, message: "Required" }]}>
                  <Select placeholder="Select status" size="large">
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                    <Option value="draft">Draft</Option>
                  </Select>
                </Form.Item>
              </div>

              <Form.Item label="Seller Name" name="sellerName">
                <Input placeholder="Seller name (optional)" size="large" />
              </Form.Item>
            </div>
          </div>

          {/* ══════════════════════════════════
              COLUMN 2 — Pricing + Image
          ══════════════════════════════════ */}
          <div className="apf-col">

            {/* Section: Retail Pricing */}
            <div className="apf-card">
              <div className="apf-card-title">
                Retail Pricing
                <span className="apf-card-badge retail">Customer</span>
              </div>
              <p className="apf-card-hint">
                Enter any two fields — the third is calculated automatically.
              </p>

              <Form.Item label="Actual Price / MRP (₹)" name="actualPrice"
                rules={[{ required: true, message: "Required" }]}>
                <InputNumber
                  min={0} size="large" style={{ width: "100%" }}
                  placeholder="Original MRP"
                  prefix="₹"
                  onChange={onActualPriceChange}
                />
              </Form.Item>

              <div className="apf-row apf-price-row">
                <Form.Item label="Retail Discount (%)" name="discount">
                  <InputNumber
                    min={0} max={100} size="large" style={{ width: "100%" }}
                    placeholder="Discount %"
                    addonAfter="%"
                    onChange={onRetailDiscountChange}
                  />
                </Form.Item>

                <div className="apf-price-arrow">→</div>

                <Form.Item label="Final Retail Price (₹)" name="price"
                  rules={[{ required: true, message: "Required" }]}>
                  <InputNumber
                    min={0} size="large" style={{ width: "100%" }}
                    placeholder="Auto-calculated"
                    prefix="₹"
                    onChange={onRetailPriceChange}
                  />
                </Form.Item>
              </div>

              <div className="apf-calc-note">
                Change <strong>Discount %</strong> → Price updates &nbsp;|&nbsp;
                Change <strong>Price</strong> → Discount % updates
              </div>
            </div>

            {/* Section: Distributor Pricing */}
            <div className="apf-card">
              <div className="apf-card-title">
                Distributor Pricing
                <span className="apf-card-badge dist">Distributor</span>
              </div>
              <p className="apf-card-hint">
                Based on the same Actual Price / MRP set above.
              </p>

              <div className="apf-row apf-price-row">
                <Form.Item label="Distributor Discount (%)" name="distributorDiscount">
                  <InputNumber
                    min={0} max={100} size="large" style={{ width: "100%" }}
                    placeholder="Discount %"
                    addonAfter="%"
                    onChange={onDistDiscountChange}
                  />
                </Form.Item>

                <div className="apf-price-arrow">→</div>

                <Form.Item label="Distributor Price (₹)" name="distributorPrice"
                  rules={[{ required: true, message: "Required" }]}>
                  <InputNumber
                    min={0} size="large" style={{ width: "100%" }}
                    placeholder="Auto-calculated"
                    prefix="₹"
                    onChange={onDistPriceChange}
                  />
                </Form.Item>
              </div>

              <div className="apf-calc-note">
                Change <strong>Discount %</strong> → Price updates &nbsp;|&nbsp;
                Change <strong>Price</strong> → Discount % updates
              </div>
            </div>

            {/* Section: Product Image */}
            <div className="apf-card">
              <div className="apf-card-title">Product Image</div>

              <Upload
                beforeUpload={(f) => { setFile(f); return false; }}
                maxCount={1}
                accept="image/*"
                listType="picture-card"
                showUploadList={!!file}
              >
                {!file && (
                  <div className="apf-upload-placeholder">
                    <UploadOutlined style={{ fontSize: 24, color: "#2874f0" }} />
                    <span>Click to upload</span>
                    <span className="apf-upload-hint">JPG, PNG up to 5MB</span>
                  </div>
                )}
              </Upload>

              {file && (
                <div className="apf-file-selected">
                  ✓ {file.name}
                  <button type="button" className="apf-file-remove" onClick={() => setFile(null)}>✕</button>
                </div>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              className={`apf-submit-btn ${loading ? "loading" : ""}`}
              disabled={loading}
            >
              {loading ? "Adding Product..." : "Add Product"}
            </button>
          </div>

        </div>
      </Form>
    </div>
  );
};

export default AddPerfumeForm;